/**
 * The matchup board, drawn: one row per battle, an HP bar on every Pokémon, and the player's bench
 * in a strip under the starters (slice 8c). Slice 8d: the player is always the left column (YOU,
 * with SUB and MOVE), the opponent the right (THEM); the header says who is on offense. Each row
 * is labelled with its battle in plain words, and the middle cell is a win bar (your share green,
 * theirs red), the % you win, and one reason line. Read-only in the sheet; in the Book (slice 8b)
 * each row's SUB button opens the bench for the player's slot: first those who flip the row, then
 * everyone else for rotation, each with the chance it would have there. MOVE (slice 8c) opens the
 * player's other on-field Pokémon to swap with.
 */
import type { DefenseSlot, OffenseSlot, Status, TypeName } from '@tdmons/sim';
import type {
  BenchView,
  BoardRow,
  BoardSide,
  MoveOption,
  SubOption,
  SubOptions,
} from '../game/board';
import type { TheirChanges } from '../game/aiChanges';
import { KIND_LABEL } from '../game/reason';
import { hpBar } from './hp';
import { LIGHT_TYPES } from './types';

/**
 * Three-letter type codes for the matchup board (slice 11b), so two chips fit on one line in a
 * phone column; the chip keeps the full name for screen readers and on hover. Everywhere else
 * (Moves, Bag, the roster screen, the Dex) chips spell the type out.
 */
export const TYPE_CODE: Record<TypeName, string> = {
  Normal: 'NRM',
  Fire: 'FIR',
  Water: 'WTR',
  Electric: 'ELE',
  Grass: 'GRS',
  Ice: 'ICE',
  Fighting: 'FGT',
  Poison: 'PSN',
  Ground: 'GRD',
  Flying: 'FLY',
  Psychic: 'PSY',
  Bug: 'BUG',
  Rock: 'RCK',
  Ghost: 'GST',
  Dragon: 'DRG',
  Dark: 'DRK',
  Steel: 'STL',
  Fairy: 'FAI',
};

/** A type chip; light types take dark text for contrast (ui-style-guide "Color"). */
export function typeChip(t: TypeName): HTMLElement {
  const chip = document.createElement('span');
  chip.className = LIGHT_TYPES.has(t) ? 'chip light' : 'chip';
  chip.style.background = `var(--type-${t.toLowerCase()})`;
  chip.textContent = t;
  return chip;
}

/** The board's type chip (slice 11b): the three-letter code, the full name on hover and read out. */
export function typeCodeChip(t: TypeName): HTMLElement {
  const chip = typeChip(t);
  chip.textContent = TYPE_CODE[t];
  chip.title = t;
  chip.setAttribute('aria-label', t);
  return chip;
}

/** A small triangle pointing at the column the reason favors (you: left, them: right). */
const POINT = {
  left: 'M0 4L6 0V8Z',
  right: 'M6 4L0 0V8Z',
};

const STATUS_TAG: Record<Status, string> = {
  paralysis: 'PAR',
  burn: 'BRN',
  sleep: 'SLP',
  confusion: 'CNF',
};

/** A status chip (PAR, BRN, SLP, CNF) in its fixed color. */
export function statusChip(s: Status): HTMLElement {
  const chip = document.createElement('span');
  chip.className = `chip status status-${STATUS_TAG[s].toLowerCase()}`;
  chip.textContent = STATUS_TAG[s];
  return chip;
}

const pct = (x: number) => `${Math.round(x * 100)}%`;

/** One side of a row (slice 11b): the name, then the slot beside the HP bar, then the type codes. */
function mon(s: BoardSide, cls: string, tag?: string): HTMLElement {
  const box = document.createElement('div');
  box.className = `board-mon ${cls}`;
  const name = document.createElement('span');
  name.className = 'board-name';
  name.textContent = s.name;
  const line = document.createElement('span');
  line.className = 'board-line';
  const slot = document.createElement('span');
  slot.className = 'board-slot';
  slot.textContent = s.slot;
  line.append(slot, hpBar(s.hp, s.maxHp));
  const chips = document.createElement('span');
  chips.className = 'board-chips';
  chips.append(...s.types.map(typeCodeChip));
  box.append(name, line, chips);
  if (tag) {
    const t = document.createElement('span');
    t.className = 'board-tag';
    t.textContent = tag;
    box.append(t);
  }
  return box;
}

function point(dir: 'left' | 'right'): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 6 8');
  svg.setAttribute('aria-hidden', 'true');
  svg.classList.add('reason-point');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', POINT[dir]);
  svg.append(path);
  return svg;
}

/**
 * The middle cell: the battle in plain words (slice 11b: here, not on a line of its own), the win
 * bar (your share green, from the left), the % you win, the reason.
 */
function winCell(row: BoardRow): HTMLElement {
  const box = document.createElement('div');
  box.className = 'board-win';
  box.setAttribute('role', 'img');
  const kind = document.createElement('span');
  kind.className = 'board-kind';
  kind.textContent = KIND_LABEL[row.kind];
  const bar = document.createElement('span');
  bar.className = 'win-bar';
  const ours = document.createElement('i');
  ours.style.width = `${(row.chance * 100).toFixed(1)}%`;
  bar.append(ours);
  const chance = document.createElement('span');
  chance.className = 'win-pct';
  chance.textContent = `YOU WIN ${pct(row.chance)}`;
  const reason = document.createElement('span');
  reason.className = `board-reason ${row.reason.favors}`;
  const text = document.createElement('span');
  text.textContent = row.reason.text;
  if (row.reason.favors === 'you') reason.append(point('left'), text);
  else if (row.reason.favors === 'them') reason.append(text, point('right'));
  else reason.append(text);
  box.append(kind, bar, chance, reason);
  const why =
    row.reason.favors === 'even'
      ? 'even'
      : `${row.reason.favors === 'you' ? 'your' : 'their'} edge: ${row.reason.text}`;
  box.setAttribute('aria-label', `${KIND_LABEL[row.kind]}: you win ${pct(row.chance)}, ${why}`);
  return box;
}

type Slot = OffenseSlot | DefenseSlot;

/** The Book's SUB column: the bench for the player's slot on each row, and MOVE under it. */
export interface BoardSubs {
  options(slot: Slot): SubOptions;
  /** The slot whose list is open, if any, and which list. */
  open: Slot | null;
  openList?: 'sub' | 'move';
  /** Slots with a drafted sub, and who that slot had before it. */
  drafted: ReadonlyMap<Slot, string>;
  toggle(slot: Slot): void;
  pick(slot: Slot, index: number): void;
  keep(slot: Slot): void;
  /** MOVE (slice 8c): the other on-field Pokémon this slot could trade places with. */
  moves(slot: Slot): MoveOption[];
  /** Slots in a drafted MOVE, with their partner. */
  moved: ReadonlyMap<Slot, Slot>;
  toggleMove(slot: Slot): void;
  swap(slot: Slot, other: Slot): void;
  unswap(slot: Slot): void;
}

function optionButton(o: SubOption, flips: boolean, pick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = flips ? 'btn sub-option flips' : 'btn sub-option';
  const name = document.createElement('span');
  name.className = 'sub-name';
  name.textContent = o.name;
  const chips = document.createElement('span');
  chips.className = 'board-chips';
  chips.append(...o.types.map(typeChip), ...(o.status ? [statusChip(o.status)] : []));
  const hp = document.createElement('span');
  hp.className = 'sub-hp';
  hp.textContent = `HP ${pct(o.hp / o.maxHp)}`;
  const chance = document.createElement('span');
  chance.className = 'sub-chance';
  chance.textContent = `→ ${pct(o.chance)}`;
  b.append(name, chips, hp, chance);
  b.setAttribute(
    'aria-label',
    `${o.name}, HP ${pct(o.hp / o.maxHp)}${o.status ? `, ${o.status}` : ''}, you would win ${pct(o.chance)}`,
  );
  b.addEventListener('click', pick);
  return b;
}

function subList(slot: Slot, subs: BoardSubs, list: SubOptions): HTMLElement {
  const box = document.createElement('div');
  box.className = 'sub-list';
  box.id = `sub-list-${slot}`;
  const group = (title: string, items: readonly SubOption[], flips: boolean) => {
    if (items.length === 0) return;
    const h = document.createElement('p');
    h.className = 'sub-group';
    h.textContent = title;
    box.append(h, ...items.map((o) => optionButton(o, flips, () => subs.pick(slot, o.index))));
  };
  group('FLIPS', list.flips, true);
  group('REST HIM', list.rest, false);
  const before = subs.drafted.get(slot);
  if (before !== undefined) {
    const keep = document.createElement('button');
    keep.type = 'button';
    keep.className = 'btn sub-keep';
    keep.textContent = `Keep ${before}`;
    keep.addEventListener('click', () => subs.keep(slot));
    box.append(keep);
  }
  return box;
}

function moveList(slot: Slot, subs: BoardSubs): HTMLElement {
  const box = document.createElement('div');
  box.className = 'sub-list move-list';
  box.id = `move-list-${slot}`;
  const partner = subs.moved.get(slot);
  if (partner !== undefined) {
    const undo = document.createElement('button');
    undo.type = 'button';
    undo.className = 'btn sub-keep';
    undo.textContent = `Undo move with ${partner}`;
    undo.addEventListener('click', () => subs.unswap(slot));
    box.append(undo);
    return box;
  }
  const h = document.createElement('p');
  h.className = 'sub-group';
  h.textContent = `SWAP ${slot} WITH`;
  box.append(h);
  for (const o of subs.moves(slot)) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn sub-option move-option';
    b.dataset.slot = o.slot;
    const name = document.createElement('span');
    name.className = 'sub-name';
    name.textContent = `${o.slot} · ${o.name}`;
    const chips = document.createElement('span');
    chips.className = 'board-chips';
    chips.append(...o.types.map(typeChip));
    const hp = document.createElement('span');
    hp.className = 'sub-hp';
    hp.textContent = `HP ${pct(o.hp / o.maxHp)}`;
    const chance = document.createElement('span');
    chance.className = 'sub-chance';
    chance.textContent = `→ ${pct(o.chance)}`;
    b.append(name, chips, hp, chance);
    b.setAttribute(
      'aria-label',
      `Swap with ${o.name} at ${o.slot}, HP ${pct(o.hp / o.maxHp)}, you would win ${pct(o.chance)} at ${slot}`,
    );
    b.addEventListener('click', () => subs.swap(slot, o.slot));
    box.append(b);
  }
  return box;
}

/** The player's bench under the starters (slice 8c): name, types, HP, and the positions it plays. */
function benchStrip(bench: readonly BenchView[]): HTMLElement {
  const box = document.createElement('section');
  box.className = 'bench-strip';
  box.setAttribute('aria-label', 'Bench');
  const h = document.createElement('p');
  h.className = 'bench-head';
  h.textContent = 'BENCH';
  box.append(h);
  if (bench.length === 0) {
    const none = document.createElement('p');
    none.className = 'bench-none';
    none.textContent = 'No one on the bench.';
    box.append(none);
  }
  for (const m of bench) {
    const row = document.createElement('div');
    row.className = 'bench-mon';
    const name = document.createElement('span');
    name.className = 'board-name';
    name.textContent = m.name;
    const chips = document.createElement('span');
    chips.className = 'board-chips';
    chips.append(...m.types.map(typeChip), ...(m.status ? [statusChip(m.status)] : []));
    const pos = document.createElement('span');
    pos.className = 'bench-pos';
    pos.textContent = m.positions ? m.positions.join(' · ') : 'ANY';
    pos.setAttribute(
      'aria-label',
      m.positions ? `plays ${m.positions.join(' or ')}` : 'plays any position',
    );
    const hp = document.createElement('span');
    hp.className = 'bench-hp';
    hp.textContent = `HP ${m.hp}/${m.maxHp}${m.sitPlays > 0 ? ` · SITS ${m.sitPlays}` : ''}`;
    row.append(name, chips, pos, hpBar(m.hp, m.maxHp), hp);
    if (m.alsoOn) {
      // A two-way player: subbing him in here keeps him on the other side's chart.
      const also = document.createElement('span');
      also.className = 'bench-also';
      also.textContent = `ALSO ON ${m.alsoOn === 'defense' ? 'D' : 'O'}`;
      also.setAttribute('aria-label', `also starts on ${m.alsoOn}`);
      row.append(also);
    }
    box.append(row);
  }
  return box;
}

/**
 * `theirs` (slice 11c): the other side's changes this call, the AI offense's subs and moves when
 * the player defends; their rows carry the same NEW · NO MOVE / MOVED tags as the player's.
 */
export function renderBoard(
  root: HTMLElement,
  rows: readonly BoardRow[],
  subs?: BoardSubs,
  bench?: readonly BenchView[],
  theirs?: TheirChanges,
): void {
  const head = document.createElement('div');
  head.className = 'board-head';
  const seat = rows[0]?.side ?? 'offense';
  const other = seat === 'offense' ? 'defense' : 'offense';
  for (const t of [`YOU · ${seat.toUpperCase()}`, '', `THEM · ${other.toUpperCase()}`]) {
    const s = document.createElement('span');
    s.textContent = t;
    head.append(s);
  }
  const list = rows.flatMap((row) => {
    const r = document.createElement('div');
    r.className = 'board-row';
    r.dataset.kind = row.kind;
    const mine = row.you.slot;
    const fresh = subs?.drafted.has(mine)
      ? 'NEW · NO MOVE'
      : subs?.moved.has(mine)
        ? 'MOVED'
        : undefined;
    const you = mon(row.you, `you ${seat}`, fresh);
    const them = row.them.slot;
    const changed = theirs?.drafted.has(them)
      ? 'NEW · NO MOVE'
      : theirs?.moved.has(them)
        ? 'MOVED'
        : undefined;
    r.append(you, winCell(row), mon(row.them, `them ${other}`, changed));
    if (!subs) return [r];
    const sub = document.createElement('span');
    sub.className = 'board-sub';
    you.append(sub);
    const options = subs.options(mine);
    const movedHere = subs.moved.has(mine);
    const subOpen = subs.open === mine && subs.openList !== 'move';
    const moveOpen = subs.open === mine && subs.openList === 'move';
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn btn-sub';
    b.textContent = 'SUB';
    b.dataset.slot = mine;
    // A row in a drafted MOVE takes no sub until the move is undone, and the other way round.
    const any =
      !movedHere && (options.flips.length + options.rest.length > 0 || subs.drafted.has(mine));
    b.disabled = !any;
    b.setAttribute('aria-expanded', String(subOpen && any));
    b.setAttribute('aria-controls', `sub-list-${mine}`);
    b.setAttribute(
      'aria-label',
      any
        ? `Sub at ${mine}${options.flips.length ? `, ${options.flips.length} would flip it` : ''}`
        : movedHere
          ? `Sub at ${mine}: undo the move first`
          : `Sub at ${mine}: no one on the bench can come in`,
    );
    b.addEventListener('click', () => subs.toggle(mine));
    const m = document.createElement('button');
    m.type = 'button';
    m.className = 'btn btn-move';
    m.textContent = 'MOVE';
    m.dataset.slot = mine;
    const canMove = !subs.drafted.has(mine) && (movedHere || subs.moves(mine).length > 0);
    m.disabled = !canMove;
    m.setAttribute('aria-expanded', String(moveOpen && canMove));
    m.setAttribute('aria-controls', `move-list-${mine}`);
    m.setAttribute(
      'aria-label',
      subs.drafted.has(mine)
        ? `Move ${mine}: take back the sub first`
        : `Move ${mine} to another spot on the field`,
    );
    m.addEventListener('click', () => subs.toggleMove(mine));
    sub.append(b, m);
    if (subOpen && any) return [r, subList(mine, subs, options)];
    if (moveOpen && canMove) return [r, moveList(mine, subs)];
    return [r];
  });
  root.replaceChildren(head, ...list, ...(bench ? [benchStrip(bench)] : []));
}
